import { reactive } from 'vue';

/**
 * Whether the desktop sidebar is folded to its icon rail (#540). AppShell owns
 * the toggle (the handle on the seam and the `[` key) and Sidebar renders the
 * rail, but the two are unrelated in every AppChrome host — Sidebar is slotted
 * into AppShell's `#sidebar` — so, like `lumiDockLauncher`, a module-scoped
 * reactive singleton bridges them without touching the seven hosts' wiring.
 *
 * Remembered per viewer in localStorage. The suite shares one origin in
 * production, so folding the rail in one module folds it in all of them. The
 * mobile drawer ignores it: it always shows the full sidebar.
 */
const STORAGE_KEY = 'lumi:sidebarCollapsed';

function fromStorage(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return localStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export const sidebarCollapse = reactive({ collapsed: fromStorage() });

export function setSidebarCollapsed(value: boolean): void {
  sidebarCollapse.collapsed = value;
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(STORAGE_KEY, value ? '1' : '0');
  } catch {
    // Storage can be unavailable (private mode, blocked site data): the rail
    // still folds, only the memory of it is lost.
  }
}

export function toggleSidebarCollapsed(): void {
  setSidebarCollapsed(!sidebarCollapse.collapsed);
}
